import {
	Box,
	Button,
	Stack,
} from '@mui/material';
import { useMemo, type ReactNode } from 'react';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { DetailsModalShell } from 'components/DetailsModal/DetailsModalShell';
import { FormButton } from 'theme/elements/Buttons';
import { FormCard } from 'theme/elements/Forms';
import { WebhookMonitoringFailedWebhookStatuses } from 'constants/webhook-monitoring.constants';
import { getWebhookMonitoringBusinessSummary, resolveGeneralTranslationLabel } from 'utils/webhook-monitoring.utils';
import { DetailsAttemptsTable } from './DetailsAttemptsTable';
import { DetailsBusinessSection } from './DetailsBusinessSection';
import { DetailsLoading } from './DetailsLoading';
import { DetailsAnimatedSection } from './DetailsAnimatedSection';
import { DetailsOverview } from './DetailsOverview';
import { DetailsTechnicalSection } from './DetailsTechnicalSection';

type Props = {
	open: boolean;
	row: any;
	detailsState: {
		data: any;
		loading: boolean;
	};
	onClose: () => void;
	onReplay: (operation: any) => void;
};

export const DetailsDialog = ({ open, row, detailsState, onClose, onReplay }: Props) => {
	const t = useAppSelector(selectTranslations);

	/** Field grids: slightly more air between cells than the stock form gap-2. */
	const summaryPanelSx = useMemo(
		() => ({
			display: 'grid',
			gridTemplateColumns: '1fr 1fr',
			gap: 2,
			columnGap: 2.5,
			rowGap: 2.5,
		}),
		[]
	);

	const operation = detailsState.data?.operation;
	const attempts = detailsState.data ? detailsState.data.attempts : [];
	const attemptsTotal = detailsState.data?.attemptsTotal;
	const isFailed = Boolean(operation && WebhookMonitoringFailedWebhookStatuses.includes(operation.status));
	const titleContextRow = detailsState.data ? detailsState.data.row : row;
	const titleOperationTypeDisplay = resolveGeneralTranslationLabel(titleContextRow?.operationType);

	const summaryRow = detailsState.data ? detailsState.data.row : row;

	const businessSummaryRows = useMemo(() => {
		if (!detailsState.data) {
			return [];
		}

		return getWebhookMonitoringBusinessSummary(
			detailsState.data.detailType,
			detailsState.data.businessData,
			detailsState.data.row.operationType
		);
	}, [detailsState.data]);

	const showReplayButton =
		open && Boolean(operation?.replayable) && !detailsState.loading && detailsState.data != null;

	let dialogBody: ReactNode = null;
	if (detailsState.loading) {
		dialogBody = <DetailsLoading />;
	} else if (!detailsState.data) {
		dialogBody = null;
	} else {
		const hasLastError = Boolean(isFailed && operation?.lastError);
		const hasBusiness = businessSummaryRows.length > 0;
		const technicalStagger = 1 + (hasBusiness ? 1 : 0);
		const attemptsStagger = technicalStagger + 1;
		const attemptsTitle = `${t('page.webhookMonitoring.attempts.title')} (${attemptsTotal})`;
		dialogBody = (
			<Stack spacing={4} sx={{ width: '100%', WebkitFontSmoothing: 'antialiased' }}>
				<DetailsAnimatedSection staggerIndex={0}>
					<FormCard
						title={t('page.webhookMonitoring.details.section.overview')}
						cardStyle={{ width: '100%' }}
					>
						<DetailsOverview
							operation={operation}
							summaryRow={summaryRow}
							attemptsTotal={attemptsTotal}
							t={t}
							summaryPanelSx={summaryPanelSx}
							showLastError={hasLastError}
						/>
					</FormCard>
				</DetailsAnimatedSection>
				{hasBusiness ? (
					<DetailsAnimatedSection staggerIndex={1}>
						<FormCard
							title={t('page.webhookMonitoring.details.section.business')}
							cardStyle={{ width: '100%' }}
						>
							<DetailsBusinessSection rows={businessSummaryRows} t={t} summaryPanelSx={summaryPanelSx} />
						</FormCard>
					</DetailsAnimatedSection>
				) : null}
				<DetailsAnimatedSection staggerIndex={technicalStagger}>
					<FormCard
						title={t('page.webhookMonitoring.details.section.technical')}
						cardStyle={{ width: '100%' }}
					>
						<DetailsTechnicalSection operation={operation} t={t} summaryPanelSx={summaryPanelSx} />
					</FormCard>
				</DetailsAnimatedSection>
				<DetailsAnimatedSection staggerIndex={attemptsStagger}>
					<FormCard title={attemptsTitle} cardStyle={{ width: '100%' }}>
						<DetailsAttemptsTable attempts={attempts} t={t} />
					</FormCard>
				</DetailsAnimatedSection>
			</Stack>
		);
	}

	return (
		<DetailsModalShell
			open={open}
			onClose={onClose}
			ariaLabelledBy="webhook-details-dialog-title"
			closeLabel={t('label.close')}
			title={row?.companyName}
			subtitle={
				titleOperationTypeDisplay ? (
					<>
						<Box component="span" sx={{ color: 'root.basicDeeoGreen' }}>
							{t('page.webhookMonitoring.field.operationType')}:
						</Box>{' '}
						<Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
							{titleOperationTypeDisplay}
						</Box>
					</>
				) : null
			}
			scroll="paper"
			footer={
				<Stack direction="row" justifyContent="flex-end" width="100%" flexWrap="wrap" spacing={1.25}>
					<FormButton>
						<Button
							variant="outlined"
							color="inherit"
							onClick={onClose}
							sx={{
								transition:
									'transform 0.2s cubic-bezier(0.2, 0, 0, 1), background-color 0.2s ease, border-color 0.2s ease',
								'&:active': { transform: 'scale(0.96)' },
							}}
						>
							{t('label.close')}
						</Button>
					</FormButton>
					{showReplayButton ? (
						<FormButton>
							<Button
								variant="contained"
								onClick={() => onReplay(operation)}
								sx={{
									transition:
										'transform 0.2s cubic-bezier(0.2, 0, 0, 1), background-color 0.2s ease, box-shadow 0.2s ease',
									'&:active': { transform: 'scale(0.96)' },
								}}
							>
								{t('page.webhookMonitoring.actions.replay')}
							</Button>
						</FormButton>
					) : null}
				</Stack>
			}
		>
			{dialogBody}
		</DetailsModalShell>
	);
};
